import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Event types that can be received from the event stream.
 */
export type RealtimeEventType =
    | 'activity_created'
    | 'incoming_call'
    | 'call_ended'
    | 'contact_updated'
    | 'customer_account_updated'
    | 'invoice_updated'
    | 'notification'
    | 'error';

/**
 * Base interface for all realtime events.
 */
export interface RealtimeEvent<T = Record<string, unknown>> {
    id: number;
    eventType: RealtimeEventType;
    channel: string;
    payload: T;
    priority: number;
    createdAt: string;
}

/**
 * Activity event payload.
 */
export interface ActivityEventPayload {
    activityIdentifier: string;
    source: string;
    subjectType: 'contact' | 'customer_account';
    subjectIdentifier: string;
    type: string;
    occurredAt: string;
    actorName: string | null;
}

/**
 * Incoming call event payload (for future Sipgate integration).
 */
export interface IncomingCallPayload {
    callId: string;
    callerNumber: string;
    callerName?: string;
    contactIdentifier?: string;
    avatarUrl?: string;
}

/**
 * Connection state for the event stream.
 */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Options for the useEventStream hook.
 */
interface UseEventStreamOptions {
    /**
     * Channels to subscribe to.
     * Default: ['global']
     *
     * Available channels:
     * - 'global' - All events (always included)
     * - 'activities' - Activity stream events
     * - 'calls' - Phone call events
     * - 'contact:{identifier}' - Events for a specific contact
     * - 'customer_account:{identifier}' - Events for a specific customer account
     */
    channels?: string[];

    /** Callback for activity events */
    onActivity?: (payload: ActivityEventPayload) => void;

    /** Callback for incoming call events */
    onIncomingCall?: (payload: IncomingCallPayload) => void;

    /** Callback for any event (generic handler) */
    onEvent?: (event: RealtimeEvent) => void;

    /** Callback when connection state changes */
    onConnectionChange?: (state: ConnectionState) => void;

    /** Whether the stream should be active (default: true) */
    enabled?: boolean;
}

/**
 * Hook for subscribing to realtime events via Server-Sent Events.
 *
 * Provides a unified interface for receiving all types of realtime events
 * including activities, phone calls, and entity updates.
 *
 * @example
 * ```tsx
 * // Subscribe to activities for a specific contact
 * useEventStream({
 *     channels: ['activities', 'contact:con-abc123'],
 *     onActivity: (activity) => {
 *         console.log('New activity:', activity);
 *         invalidateActivityQuery();
 *     }
 * });
 *
 * // Subscribe to incoming calls (Sipgate)
 * useEventStream({
 *     channels: ['calls'],
 *     onIncomingCall: (call) => {
 *         showIncomingCallBanner(call);
 *     }
 * });
 *
 * // Generic event handler
 * useEventStream({
 *     onEvent: (event) => {
 *         console.log('Event:', event.eventType, event.payload);
 *     }
 * });
 * ```
 */
export function useEventStream(options: UseEventStreamOptions = {}) {
    const {
        channels = ['global'],
        onActivity,
        onIncomingCall,
        onEvent,
        onConnectionChange,
        enabled = true
    } = options;

    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
    const abortControllerRef = useRef<AbortController | null>(null);
    const reconnectTimeoutRef = useRef<number | null>(null);
    const lastEventIdRef = useRef<string | null>(null);
    const channelsRef = useRef(channels);

    // Update channels ref when channels change
    useEffect(() => {
        channelsRef.current = channels;
    }, [channels]);

    const updateConnectionState = useCallback((state: ConnectionState) => {
        setConnectionState(state);
        onConnectionChange?.(state);
    }, [onConnectionChange]);

    const handleEvent = useCallback((event: RealtimeEvent) => {
        // Call generic handler
        onEvent?.(event);

        // Call type-specific handlers
        switch (event.eventType) {
            case 'activity_created':
                onActivity?.(event.payload as unknown as ActivityEventPayload);
                break;
            case 'incoming_call':
                onIncomingCall?.(event.payload as unknown as IncomingCallPayload);
                break;
        }
    }, [onEvent, onActivity, onIncomingCall]);

    const connect = useCallback(async () => {
        if (!isAuthenticated || !enabled) {
            return;
        }

        // Cancel any existing connection
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        updateConnectionState('connecting');

        try {
            const token = await getAccessTokenSilently();

            // Build the URL with channels
            const channelParam = channelsRef.current.join(',');
            const url = `/api/events?channels=${encodeURIComponent(channelParam)}`;

            const headers: Record<string, string> = {
                'Accept': 'text/event-stream',
                'Authorization': `Bearer ${token}`,
                'Cache-Control': 'no-cache',
            };

            // Include Last-Event-ID for reconnection
            if (lastEventIdRef.current) {
                headers['Last-Event-ID'] = lastEventIdRef.current;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers,
                signal: abortControllerRef.current.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            updateConnectionState('connected');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                // Process complete events from buffer
                const events = buffer.split('\n\n');
                buffer = events.pop() || ''; // Keep incomplete event in buffer

                for (const eventStr of events) {
                    if (!eventStr.trim()) continue;

                    const parsedEvent = parseSSEEvent(eventStr);

                    if (parsedEvent.id) {
                        lastEventIdRef.current = parsedEvent.id;
                    }

                    // Skip comments and heartbeats
                    if (!parsedEvent.type || !parsedEvent.data) continue;

                    // Handle error events
                    if (parsedEvent.type === 'error') {
                        console.error('SSE error event:', parsedEvent.data);
                        continue;
                    }

                    try {
                        const eventData = JSON.parse(parsedEvent.data) as RealtimeEvent;
                        handleEvent(eventData);
                    } catch (e) {
                        console.error('Failed to parse event:', e);
                    }
                }
            }

            // Stream ended normally (timeout), schedule reconnect
            updateConnectionState('disconnected');
            scheduleReconnect();

        } catch (error) {
            const err = error as Error;

            // Connection was intentionally aborted (e.g., component unmount)
            if (err.name === 'AbortError') {
                updateConnectionState('disconnected');
                return;
            }

            // "Error in input stream" happens when the page is reloaded while streaming
            // This is expected behavior, not a real error
            if (err.name === 'TypeError' && err.message?.includes('input stream')) {
                updateConnectionState('disconnected');
                return;
            }

            console.error('Event stream error:', error);
            updateConnectionState('error');
            scheduleReconnect();
        }
    }, [isAuthenticated, enabled, getAccessTokenSilently, handleEvent, updateConnectionState]);

    const scheduleReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        // Reconnect after 3 seconds (matching server's retry hint)
        reconnectTimeoutRef.current = window.setTimeout(() => {
            connect();
        }, 3000);
    }, [connect]);

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        updateConnectionState('disconnected');
    }, [updateConnectionState]);

    // Store connect in a ref to avoid dependency issues in useEffect
    const connectRef = useRef(connect);
    connectRef.current = connect;

    // Connection management effect
    // Using channelsKey as dependency ensures we reconnect when channels change
    const channelsKey = channels.join(',');
    useEffect(() => {
        if (!enabled || !isAuthenticated) {
            return;
        }

        // Connect (or reconnect if channels changed)
        connectRef.current();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
        };
    }, [enabled, isAuthenticated, channelsKey]);

    return {
        connectionState,
        disconnect,
        reconnect: connect,
    };
}

/**
 * Parse a Server-Sent Event string into its components.
 */
function parseSSEEvent(eventStr: string): { type?: string; data?: string; id?: string } {
    const lines = eventStr.split('\n');
    const event: { type?: string; data?: string; id?: string } = {};

    for (const line of lines) {
        if (line.startsWith('event:')) {
            event.type = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
            event.data = line.slice(5).trim();
        } else if (line.startsWith('id:')) {
            event.id = line.slice(3).trim();
        }
        // Ignore comments (lines starting with ':')
    }

    return event;
}

export default useEventStream;
